import { useSelector } from 'react-redux';
import sum from 'lodash/sum';
import { random, range } from 'lodash';
import { Instrument } from 'services/api/responses/responseTypes';
import { Instruments } from 'services/store/ai/types';
import {
  selectMicroGenre,
  selectPrimaryGenre,
} from 'services/store/ai/selectors';
import useGenre from 'pages/AIMusicGenerator/useGenre';

export const MAX_INSTRUMENT_UNITS = 7;
export const MIN_RANDOMISATION_INSTRUMENT_UNITS = 3;

export type Value = Instruments['value'];

export interface InstrumentsSelectorProps {
  value: Value;
  onChange: (value: Value) => void;
}

const useInstrumentsSelector = ({
  value,
  onChange,
}: InstrumentsSelectorProps) => {
  const primaryGenre = useSelector(selectPrimaryGenre);
  const microGenre = useSelector(selectMicroGenre);

  const genreData = useGenre(primaryGenre, microGenre);
  const availableInstruments = genreData?.instruments || [];

  const changeInstrumentCount = (instrument: Instrument, newValue: number) => {
    onChange({
      ...value,
      [instrument]: newValue,
    });
  };

  const randomise = () => {
    const valuesCount = random(
      MIN_RANDOMISATION_INSTRUMENT_UNITS,
      MAX_INSTRUMENT_UNITS
    );

    const randomisedValue = range(valuesCount).reduce((accumulator) => {
      const genreIndex = random(0, availableInstruments.length - 1);
      const genre = availableInstruments[genreIndex];
      const currentCount = accumulator[genre] || 0;
      return {
        ...accumulator,
        [genre]: currentCount + 1,
      };
    }, {} as Value);

    onChange(randomisedValue);
  };

  const totalUnits = sum(Object.values(value));

  const getMaxInstrumentUnits = (instrument: Instrument) => {
    if (totalUnits === MAX_INSTRUMENT_UNITS) {
      return value[instrument] || 0;
    }
    return MAX_INSTRUMENT_UNITS;
  };

  return {
    availableInstruments,
    changeInstrumentCount,
    randomise,
    totalUnits,
    maxUnits: MAX_INSTRUMENT_UNITS,
    getMaxInstrumentUnits,
  };
};

export default useInstrumentsSelector;
